/**
 * Funnel/source channel metadata — humanized labels + two-letter abbreviations
 * for the raw seed/CRM keys (`google_ads`, `passaparola`, …). Shared between
 * the pipelines feature (deals table + SidePanel) and the lead-detail feature
 * (Source cell in the value summary) so the visual semantics stay consistent.
 *
 * `FunnelChannel` is a UI mock for the future first-class "Funnel" entity
 * (marketing channel / lead source). Until that lands, this lookup is the
 * source of truth for display naming. Channels share one neutral colour
 * because the abbreviation carries channel identity, not a colour scale —
 * Google Ads vs Organic vs Event are independent, not ordered.
 */

export interface FunnelChannel {
  key: string
  /** Display label, e.g. "Google Ads". */
  label: string
  /** Two-letter code rendered in the source-cell badge (e.g. "GA"). */
  abbr: string
  /** Neutral pill background colour; reserved for future per-channel theming. */
  color: string
}

const FUNNEL_NEUTRAL = '#2a2f3a'

export const FUNNEL_LOOKUP: Record<string, FunnelChannel> = {
  google_ads: { key: 'google_ads', label: 'Google Ads', abbr: 'GA', color: FUNNEL_NEUTRAL },
  facebook_ads: { key: 'facebook_ads', label: 'Facebook Ads', abbr: 'FB', color: FUNNEL_NEUTRAL },
  instagram: { key: 'instagram', label: 'Instagram', abbr: 'IG', color: FUNNEL_NEUTRAL },
  referral: { key: 'referral', label: 'Referral', abbr: 'RF', color: FUNNEL_NEUTRAL },
  passaparola: {
    key: 'passaparola',
    label: 'Word of mouth',
    abbr: 'WM',
    color: FUNNEL_NEUTRAL,
  },
  organic: { key: 'organic', label: 'Organic', abbr: 'OR', color: FUNNEL_NEUTRAL },
  outbound: { key: 'outbound', label: 'Outbound', abbr: 'OB', color: FUNNEL_NEUTRAL },
  event: { key: 'event', label: 'Event', abbr: 'EV', color: FUNNEL_NEUTRAL },
}

export function funnelChannelFor(key: string): FunnelChannel {
  const known = FUNNEL_LOOKUP[key]
  if (known) return known
  // Fallback for an unknown funnel key: title-case label, first two chars as
  // abbr, neutral colour. Keeps a CRM that records new channels rendering
  // without throwing while the canonical lookup catches up.
  return {
    key,
    label: titleCase(key),
    abbr: key.slice(0, 2).toUpperCase(),
    color: FUNNEL_NEUTRAL,
  }
}

function titleCase(key: string): string {
  return key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
